/**
 * Payment domain types for taxhive-webui.
 *
 * Mirrors the taxhive-payment-service contract (snake_case on the wire,
 * camelCase in the UI). The backend keys a payment by `customer_id` (integer)
 * and identifies a payment by `payment_id` (integer); both are surfaced here as
 * strings so the UI stays consistent with the rest of the app's id handling.
 */

export type PaymentStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'refunded'

export type PaymentMethod =
  | 'credit_card'
  | 'debit_card'
  | 'net_banking'
  | 'upi'
  | 'wallet'
  | 'bank_transfer'

export type PaymentGateway =
  | 'razorpay'
  | 'paytm'
  | 'stripe'
  | 'phonepe'
  | 'manual'

export type PaymentCurrency = 'INR' | 'USD' | 'EUR' | 'GBP'

/** A single payment transaction. */
export interface Payment {
  /** Backend `payment_id`, surfaced as a string. */
  id: string
  tenantId: string
  customerId: string
  /** Best-effort display name resolved from mock data; not on the backend row. */
  customerName?: string
  amount: number
  currency: PaymentCurrency
  paymentMethod: PaymentMethod
  gateway: PaymentGateway
  status: PaymentStatus
  transactionId: string | null
  providerOrderId?: string | null
  providerPaymentId?: string | null
  providerStatus?: string | null
  description: string | null
  paidAt: string | null
  createdAt: string
  updatedAt: string
}

/** Payload for creating a payment (maps to `PaymentCreate`). */
export interface CreatePaymentInput {
  customerId: string
  amount: number
  currency: PaymentCurrency
  paymentMethod: PaymentMethod
  gateway: PaymentGateway
  description?: string
}

/** Server-authoritative intent for collecting a client payment through Razorpay. */
export interface OnlinePaymentInput {
  customerId: string
  amount: number
  currency: 'INR'
  paymentMethod: PaymentMethod
  description?: string
  assessmentId?: string
}

/** Safe checkout data returned by the server. No provider secret reaches the UI. */
export interface RazorpayCheckoutOrder {
  payment: Payment
  orderRecordId: string
  providerOrderId: string
  keyId: string
  amountMinor: number
  currency: string
  receipt: string
  orderStatus: string
  expiresAt: string | null
}

/** Proof returned by Razorpay Checkout and verified exclusively by the server. */
export interface RazorpayCheckoutProof {
  providerOrderId: string
  providerPaymentId: string
  signature: string
}

export interface RazorpaySubscriptionCheckoutOrder {
  orderRecordId: string
  tenantId: string
  subscriptionId: string
  invoiceId: string
  providerOrderId: string
  keyId: string
  amountMinor: number
  currency: string
  receipt: string
  orderStatus: string
  expiresAt: string | null
}

export interface RazorpaySubscriptionPaymentResult {
  orderRecordId: string
  tenantId: string
  subscriptionId: string
  invoiceId: string
  providerOrderId: string
  providerPaymentId: string
  providerStatus: string
  orderStatus: string
  billingDeliveryStatus: string
}

/** Payload for a partial update (maps to `PaymentUpdate`). */
export interface UpdatePaymentInput {
  status?: PaymentStatus
  transactionId?: string
  description?: string
}

/** Aggregated statistics (maps to `PaymentSummary`). */
export interface PaymentStats {
  totalPayments: number
  totalAmount: number
  successfulPayments: number
  failedPayments: number
  pendingPayments: number
  currency: PaymentCurrency
}

export interface PaymentFilters {
  status: PaymentStatus | 'all'
  method: PaymentMethod | 'all'
}

export const DEFAULT_PAYMENT_FILTERS: PaymentFilters = {
  status: 'all',
  method: 'all',
}

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  pending: 'Pending',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
}

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  credit_card: 'Credit Card',
  debit_card: 'Debit Card',
  net_banking: 'Net Banking',
  upi: 'UPI',
  wallet: 'Wallet',
  bank_transfer: 'Bank Transfer',
}

export const PAYMENT_GATEWAY_LABELS: Record<PaymentGateway, string> = {
  razorpay: 'Razorpay',
  paytm: 'Paytm',
  stripe: 'Stripe',
  phonepe: 'PhonePe',
  manual: 'Manual',
}

export const PAYMENT_STATUS_OPTIONS: PaymentStatus[] = [
  'pending',
  'processing',
  'completed',
  'failed',
  'cancelled',
  'refunded',
]

export const PAYMENT_METHOD_OPTIONS: PaymentMethod[] = [
  'credit_card',
  'debit_card',
  'net_banking',
  'upi',
  'wallet',
  'bank_transfer',
]

export const PAYMENT_GATEWAY_OPTIONS: PaymentGateway[] = [
  'razorpay',
  'paytm',
  'stripe',
  'phonepe',
  'manual',
]
